/**
 * JSON helpers for payload, schema, and sample editors.
 *
 * openapi-typescript renders a free-form `type: object` as
 * `Record<string, never>`. `apiObject()` is the one place we cast an
 * arbitrary JSON object into that shape for request bodies.
 */

export type JsonObject = Record<string, unknown>
type ApiObject = Record<string, never>

export function apiObject(v: JsonObject): ApiObject
export function apiObject(v: JsonObject | null | undefined): ApiObject | null
export function apiObject(v: JsonObject | null | undefined): ApiObject | null {
  return (v ?? null) as ApiObject | null
}

export function isJsonObject(v: unknown): v is JsonObject {
  return v !== null && typeof v === 'object' && !Array.isArray(v)
}

export type ParseResult =
  { ok: true; value: JsonObject } | { ok: false; error: string; line?: number; column?: number }

/**
 * Parse editor text that must be a JSON object. Reports the 1-based line and
 * column when the engine gives a character offset.
 */
export function parseJsonObject(text: string, what = 'Payload'): ParseResult {
  if (text.trim() === '') return { ok: false, error: `${what} is empty. Enter a JSON object.` }
  try {
    const value: unknown = JSON.parse(text)
    if (!isJsonObject(value)) return { ok: false, error: `${what} must be a JSON object ({ … }).` }
    return { ok: true, value }
  } catch (e) {
    const message = e instanceof Error ? e.message : String(e)
    const pos = /position (\d+)/i.exec(message)
    if (pos) {
      const offset = Number(pos[1])
      const before = text.slice(0, offset)
      const line = before.split('\n').length
      const column = offset - before.lastIndexOf('\n')
      return { ok: false, error: `Invalid JSON at line ${line}, column ${column}.`, line, column }
    }
    const lc = /line (\d+) column (\d+)/i.exec(message)
    if (lc)
      return {
        ok: false,
        error: `Invalid JSON at line ${lc[1]}, column ${lc[2]}.`,
        line: Number(lc[1]),
        column: Number(lc[2]),
      }
    // V8 often gives no position; Monaco marks the spot in the editor anyway.
    const short = message.split(/,\s*"/)[0]?.replace(/^JSON Parse error:\s*/, '') ?? ''
    return { ok: false, error: short ? `Invalid JSON: ${short}.` : 'Invalid JSON.' }
  }
}

export function prettyJson(v: unknown): string {
  return v === undefined ? '' : JSON.stringify(v, null, 2)
}

/** Stable stringify (sorted keys) for comparing JSON values regardless of key order. */
export function canonicalJson(v: unknown): string {
  return JSON.stringify(v, (_k, val: unknown) =>
    isJsonObject(val)
      ? Object.fromEntries(Object.entries(val).sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0)))
      : val,
  )
}

/** Minimal JSON Schema meta-schema for the schema editor: catches common shape mistakes. */
export const JSON_SCHEMA_META_SCHEMA = {
  $schema: 'http://json-schema.org/draft-07/schema#',
  $id: 'mailsmith://meta/json-schema',
  definitions: {
    schema: {
      type: ['object', 'boolean'],
      properties: {
        $schema: { type: 'string' },
        $id: { type: 'string' },
        $ref: { type: 'string' },
        title: { type: 'string' },
        description: { type: 'string' },
        type: {
          anyOf: [
            { $ref: '#/definitions/simpleType' },
            { type: 'array', items: { $ref: '#/definitions/simpleType' }, minItems: 1 },
          ],
        },
        enum: { type: 'array' },
        format: { type: 'string' },
        required: { type: 'array', items: { type: 'string' }, uniqueItems: true },
        properties: { type: 'object', additionalProperties: { $ref: '#/definitions/schema' } },
        patternProperties: {
          type: 'object',
          additionalProperties: { $ref: '#/definitions/schema' },
        },
        additionalProperties: { $ref: '#/definitions/schema' },
        items: {
          anyOf: [
            { $ref: '#/definitions/schema' },
            { type: 'array', items: { $ref: '#/definitions/schema' } },
          ],
        },
        prefixItems: { type: 'array', items: { $ref: '#/definitions/schema' } },
        anyOf: { type: 'array', items: { $ref: '#/definitions/schema' }, minItems: 1 },
        oneOf: { type: 'array', items: { $ref: '#/definitions/schema' }, minItems: 1 },
        allOf: { type: 'array', items: { $ref: '#/definitions/schema' }, minItems: 1 },
        not: { $ref: '#/definitions/schema' },
        minimum: { type: 'number' },
        maximum: { type: 'number' },
        minLength: { type: 'integer', minimum: 0 },
        maxLength: { type: 'integer', minimum: 0 },
        minItems: { type: 'integer', minimum: 0 },
        maxItems: { type: 'integer', minimum: 0 },
        pattern: { type: 'string' },
        $defs: { type: 'object', additionalProperties: { $ref: '#/definitions/schema' } },
        definitions: { type: 'object', additionalProperties: { $ref: '#/definitions/schema' } },
      },
    },
    simpleType: {
      enum: ['array', 'boolean', 'integer', 'null', 'number', 'object', 'string'],
    },
  },
  $ref: '#/definitions/schema',
} as const
